import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * The breadcrumb of the top bar (planning-app.md §3 *Navigation from v3.8*, PA62a): nav item ›
 * context › page, each crumb a link, the last one the `<h1>`, the hand-off sheet's title and the
 * document title. The shell derives a default from the route table; a page that knows its context
 * — a track page, a track's tab — pushes its own with `useCrumbs` and the shell renders those.
 */
export interface Crumb {
  text: string;
  /** Where the crumb links; the last crumb never links. */
  to?: string;
  /** A proper name (a track, a document title): not translated, in its language. */
  noTranslate?: boolean;
  lang?: string;
}

interface CrumbsState {
  crumbs: Crumb[] | null;
  set: (crumbs: Crumb[] | null) => void;
}

const CrumbsContext = createContext<CrumbsState>({ crumbs: null, set: () => undefined });

export function CrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, set] = useState<Crumb[] | null>(null);
  const value = useMemo(() => ({ crumbs, set }), [crumbs]);
  return <CrumbsContext.Provider value={value}>{children}</CrumbsContext.Provider>;
}

export function usePageCrumbs(): Crumb[] | null {
  return useContext(CrumbsContext).crumbs;
}

/** A page sets its crumbs while mounted; the shell's default returns when it unmounts. */
export function useCrumbs(crumbs: Crumb[] | null): void {
  const { set } = useContext(CrumbsContext);
  // The array is rebuilt every render; its content is what matters.
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    set(key === 'null' ? null : (JSON.parse(key) as Crumb[]));
    return () => set(null);
  }, [key, set]);
}
